import React, { Dispatch, SetStateAction } from 'react';
import {
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { ExportSchema } from './exportSchema';
import { Paper } from '@shared/components';
import { ExportFileType, ExportFileTypes, ExportOptions } from './ExportDialog';
import { useMountEffect } from '@shared/hooks';
import { getLLDateWithTime } from '@retail/utils';

export const exportTotalfile = 'exportTotalfile';
interface Props {
  fileType: ExportFileType;
  defaultExportValues?: ExportOptions;
  setFileType: Dispatch<SetStateAction<ExportFileType>>;
}

export const ExportDialogForm: React.FC<Props> = ({
  fileType,
  defaultExportValues,
  setFileType,
  ...rest
}) => {
  const t = useAppTFunction();

  const { control, watch, setValue } = useFormContext<ExportSchema>();
  const allArticlesController = useController({ name: 'downloadSelection', control });
  const vilmaVersionController = useController({ name: 'vilmaVersion', control });
  const numberOfFutureDays = useController({ name: 'numberOfFutureDays', control });
  const selectedFromDate = watch('fromDate');
  const vilmaVersion = watch('vilmaVersion');

  useMountEffect(() => {
    if (!selectedFromDate) {
      allArticlesController.field.onChange('sinceLastDownload');
    } else {
      allArticlesController.field.onChange('date');
    }
    if (!vilmaVersion) {
      vilmaVersionController.field.onChange(defaultExportValues?.vilmaVersion ?? 'VILMA_27');
    }
    if (!numberOfFutureDays.field.value) {
      numberOfFutureDays.field.onChange(defaultExportValues?.futureDays);
    }
  });

  return (
    <Paper variant="outlined" paddingY="dense" {...rest}>
      <FormControl>
        <FormLabel>{t('export.selection')}</FormLabel>
        <RadioGroup defaultValue="onlyChanges">
          {selectedFromDate && (
            <FormControlLabel
              value="onlyChanges"
              control={
                <Controller
                  name="downloadSelection"
                  control={control}
                  render={({ field: { value } }) => (
                    <Radio
                      onChange={() => {
                        setValue(allArticlesController.field.name, 'date');
                      }}
                      checked={value === 'date'}
                    />
                  )}
                />
              }
              label={
                <Stack flexDirection="row" gap={0.5} alignItems="center">
                  <Typography variant="body2">{t('export.changesFromDate')}</Typography>
                  <Typography variant="subtitle2">{getLLDateWithTime(selectedFromDate)}</Typography>
                </Stack>
              }
            />
          )}

          <FormControlLabel
            value="changesSinceLastDownload"
            control={
              <Controller
                name="downloadSelection"
                control={control}
                render={({ field: { value } }) => (
                  <Radio
                    onChange={() => {
                      setValue(allArticlesController.field.name, 'sinceLastDownload');
                    }}
                    checked={value === 'sinceLastDownload'}
                  />
                )}
              />
            }
            label={<Typography variant="body2">{t('export.changesSinceLastDownload')}</Typography>}
          />

          <FormControlLabel
            value="allArticles"
            control={
              <Controller
                name="downloadSelection"
                control={control}
                render={({ field: { value } }) => (
                  <Radio
                    onChange={() => {
                      setValue(allArticlesController.field.name, 'all');
                    }}
                    checked={value === 'all'}
                  />
                )}
              />
            }
            label={<Typography variant="body2">{t('export.completeFile')}</Typography>}
          />
        </RadioGroup>
      </FormControl>

      <Divider
        sx={{
          my: 1.5,
        }}
      />

      <Typography variant="body1">{t('export.fileType')}</Typography>

      <FormControlLabel
        control={<Radio checked={fileType === ExportFileTypes.Vilma} />}
        value={t('export.vilmaFile')}
        label={<Typography variant="body2">{t('export.vilmaFile')}</Typography>}
        onChange={() => setFileType(ExportFileTypes.Vilma)}
      />

      <FormControlLabel
        control={<Radio checked={fileType === ExportFileTypes.Csv} />}
        value={t('export.csv')}
        label={<Typography variant="body2">{t('export.csv')}</Typography>}
        onChange={() => setFileType(ExportFileTypes.Csv)}
      />

      <Divider
        sx={{
          my: 1.5,
        }}
      />

      <FormControl>
        <FormLabel>{t('export.vilmaLevel')}</FormLabel>
        <RadioGroup defaultValue="VILMA_27" row>
          <FormControlLabel
            value="VILMA_27"
            control={
              <Controller
                name="vilmaVersion"
                control={control}
                render={({ field: { value } }) => (
                  <Radio
                    onChange={() => setValue(vilmaVersionController.field.name, 'VILMA_27')}
                    checked={value === 'VILMA_27'}
                  />
                )}
              />
            }
            label={<Typography variant="body2">{t('export.vilma27')}</Typography>}
          />

          <FormControlLabel
            value="VILMA_28"
            control={
              <Controller
                name="vilmaVersion"
                control={control}
                render={({ field: { value } }) => (
                  <Radio
                    onChange={() => setValue(vilmaVersionController.field.name, 'VILMA_28')}
                    checked={value === 'VILMA_28'}
                  />
                )}
              />
            }
            label={<Typography variant="body2">{t('export.vilma28')}</Typography>}
          />
        </RadioGroup>
      </FormControl>

      <Divider
        sx={{
          my: 1.5,
        }}
      />

      {/* TODO: add back when future days is ready from backend
      <FormControl sx={{ gap: 1 }}>
        <FormLabel>{`${t('export.numberOfFutureDays')} (${t(
          'export.numberOfDaysOptions'
        )})`}</FormLabel>
        <Controller
          name="numberOfFutureDays"
          control={control}
          render={() => (
            <InputWithStepper
              inputValue={numberOfFutureDays.field.value ? numberOfFutureDays.field.value + '' : ''}
              error={numberOfFutureDays.formState.errors.numberOfFutureDays?.message}
              setInputValue={(value?: string) => {
                numberOfFutureDays.field.onChange(value ?? null);
              }}
            />
          )}
        />
      </FormControl>*/}
    </Paper>
  );
};
